import { Pitcher } from "../repository/challengeRepository";

export interface SampleResult {
  pitcherId: string;
  display: string; // String(returnValue), e.g. "true" / "false" / "undefined"
  truthy: boolean;
}

export interface RunResult {
  count: number;
  trueCount: number; // pitchers for whom the function returned truthy
  total: number;
  samples: SampleResult[]; // return values for the requested example pitchers
  error?: string;
  durationMs: number;
}

// The worker body runs as an isolated script (no DOM, no app globals). It is
// still the user's own browser running the user's own code — this isolates,
// it does not "secure" against a determined user, which is fine for a learning
// sandbox where everything is client-side anyway.
const WORKER_SRC = `
self.onmessage = function (e) {
  var code = e.data.code;
  var fnName = e.data.functionName;
  var pitchers = e.data.pitchers;
  var sampleIds = e.data.sampleIds || [];
  var wantSample = {};
  for (var s = 0; s < sampleIds.length; s++) wantSample[sampleIds[s]] = true;
  var start = Date.now();
  var fn;
  try {
    // eslint-disable-next-line no-new-func
    fn = new Function(code + "\\nreturn typeof " + fnName + " === 'function' ? " + fnName + " : null;")();
  } catch (err) {
    self.postMessage({ error: "Your code did not run: " + (err && err.message ? err.message : String(err)) });
    return;
  }
  if (typeof fn !== "function") {
    self.postMessage({ error: "Could not find a function named '" + fnName + "'. Make sure you define it." });
    return;
  }
  var trueCount = 0;
  var samples = [];
  for (var i = 0; i < pitchers.length; i++) {
    var res;
    try {
      res = fn(pitchers[i].pitches);
    } catch (err) {
      self.postMessage({
        error: "Your function threw an error on " + pitchers[i].name + ": " + (err && err.message ? err.message : String(err)),
      });
      return;
    }
    if (res) trueCount++;
    if (wantSample[pitchers[i].pitcher_id]) {
      samples.push({ pitcherId: pitchers[i].pitcher_id, display: String(res), truthy: !!res });
    }
  }
  self.postMessage({ trueCount: trueCount, total: pitchers.length, samples: samples, durationMs: Date.now() - start });
};
`;

const TIMEOUT_MS = 10000;

export function runOverPitchers(
  code: string,
  functionName: string,
  pitchers: Pitcher[],
  sampleIds: string[] = []
): Promise<RunResult> {
  return new Promise((resolve) => {
    const blob = new Blob([WORKER_SRC], { type: "application/javascript" });
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);

    const cleanup = () => {
      worker.terminate();
      URL.revokeObjectURL(url);
    };

    const timer = window.setTimeout(() => {
      cleanup();
      resolve({
        count: 0,
        trueCount: 0,
        total: pitchers.length,
        samples: [],
        error: `Your code took longer than ${TIMEOUT_MS / 1000}s and was stopped. Check for an infinite loop.`,
        durationMs: TIMEOUT_MS,
      });
    }, TIMEOUT_MS);

    worker.onmessage = (e: MessageEvent) => {
      window.clearTimeout(timer);
      cleanup();
      const d = e.data || {};
      if (d.error) {
        resolve({
          count: 0,
          trueCount: 0,
          total: pitchers.length,
          samples: [],
          error: d.error,
          durationMs: d.durationMs || 0,
        });
      } else {
        resolve({
          count: d.trueCount,
          trueCount: d.trueCount,
          total: d.total,
          samples: d.samples || [],
          durationMs: d.durationMs || 0,
        });
      }
    };

    worker.onerror = (err) => {
      window.clearTimeout(timer);
      cleanup();
      resolve({
        count: 0,
        trueCount: 0,
        total: pitchers.length,
        samples: [],
        error: err.message || "Worker error",
        durationMs: 0,
      });
    };

    worker.postMessage({ code, functionName, pitchers, sampleIds });
  });
}
